import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { CoreState } from '@app/store/core/states';
import { storyBuilderActions, storyStepActions } from '@modules/story/store/actions';
import { storyBuilderSelector, storyStepSelector } from '@modules/story/store/selectors';
import { StoryBuilderState, StoryStepState } from '@modules/story/store/states';
import { Constants } from '@modules/shared/constants';

export interface RequestStoryStoreSnapshot {
  storyBuilder: StoryBuilderState;
  storyStep: StoryStepState;
}

/**
 * The Request Story modals (header and campaign page) share the story builder
 * and story step store slices with the story editor. Without a scope, picks
 * made in one modal leak into the next modal and into Submit Story, and the
 * page behind the modal loses its own state.
 */
@Injectable({ providedIn: 'root' })
export class RequestStoryStoreScope {
  /**
   * Modals currently inside the scope. Reopening Request a Story quickly opens
   * the new modal before the closing one is destroyed; without this count the
   * old modal's leave() restored the page state over the new modal (wrong
   * gallery, narrow panel) until the page was refreshed.
   */
  private open = 0;
  private pageSnapshot: RequestStoryStoreSnapshot = null;

  constructor(private store$: Store<CoreState>) { }

  /** Remember the page's story state, then start the modal clean. */
  enter(): RequestStoryStoreSnapshot {
    if (this.open === 0) {
      this.pageSnapshot = this.snapshot();
    }
    this.open++;
    this.store$.dispatch(new storyBuilderActions.ResetState());
    this.store$.dispatch(new storyStepActions.ResetState());
    return this.pageSnapshot;
  }

  /** Give the page behind the modal its story state back once the last modal closes. */
  leave(_snapshot?: RequestStoryStoreSnapshot): void {
    this.open = Math.max(0, this.open - 1);
    if (this.open > 0) {
      return;
    }
    const snapshot = this.pageSnapshot;
    this.pageSnapshot = null;
    if (!snapshot) {
      this.store$.dispatch(new storyBuilderActions.ResetState());
      this.store$.dispatch(new storyStepActions.ResetState());
      return;
    }
    this.store$.dispatch(new storyBuilderActions.RestoreState(snapshot.storyBuilder));
    this.store$.dispatch(new storyStepActions.RestoreState(snapshot.storyStep));
  }

  /**
   * Clear media and tag picks (User ↔ Guest switch) but keep the company, so
   * the gallery still knows whose galleries to load. Optionally keep media
   * and/or tag step selections.
   */
  clearSelections(
    companyId: number,
    inbound: boolean,
    keepMedia?: number[],
    keepTagSteps?: { [stepId: number]: number[] }
  ): void {
    this.store$.dispatch(new storyBuilderActions.ResetState());
    this.store$.dispatch(new storyStepActions.ResetState());
    if (companyId) {
      this.store$.dispatch(new storyBuilderActions.SetCompanyId(companyId));
    }
    this.store$.dispatch(new storyBuilderActions.SetInbound(inbound));
    if (keepMedia && keepMedia.length) {
      this.store$.dispatch(new storyBuilderActions.UpdateStoryStepSelected(Constants.storyStep.media, [...keepMedia]));
    }
    if (keepTagSteps) {
      Object.keys(keepTagSteps).forEach((stepId) => {
        const ids = keepTagSteps[stepId];
        if (ids && ids.length) {
          this.store$.dispatch(new storyBuilderActions.UpdateStoryStepSelected(+stepId, [...ids]));
        }
      });
    }
  }

  private snapshot(): RequestStoryStoreSnapshot {
    let storyBuilder: StoryBuilderState = null;
    let storyStep: StoryStepState = null;
    this.store$.select(storyBuilderSelector.selectStoryBuilderState).pipe(take(1)).subscribe(state => storyBuilder = state);
    this.store$.select(storyStepSelector.selectStoryStepState).pipe(take(1)).subscribe(state => storyStep = state);
    return storyBuilder && storyStep ? { storyBuilder, storyStep } : null;
  }
}
